import { useEffect } from 'react'
import { View } from 'react-native'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { useCountries } from 'hooks/options/useCountries'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { addressStepSchema } from '../stepSchemas'
import useCreateCustomerStore from '../useCreateCustomerStore'
import { getAddressFormDefaults } from '../utils'

interface Props {
  submitRef: React.MutableRefObject<() => void>
  onSuccess: () => void
  setIsPending?: (v: boolean) => void
}

export default function AddressStep({ submitRef, onSuccess, setIsPending }: Props) {
  const { t } = useTranslation(['user'])
  const { data, updateData } = useCreateCustomerStore()
  const config = useRemoteConfig()
  const countries = useCountries()
  const form = useForm({
    resolver: zodResolver(addressStepSchema),
    defaultValues: {
      ...getAddressFormDefaults(data, config?.region.countryCode),
    },
  })

  useEffect(() => {
    submitRef.current = form.handleSubmit(
      (values) => {
        updateData({ address: values })
        onSuccess()
      },
      () => setIsPending?.(false)
    )
  })

  const hasVat = config?.region?.vat !== 'hidden'

  return (
    <FormProvider {...form}>
      <View className="gap-8">
        {hasVat && (
          <FormInput
            control={form.control}
            name="vat"
            label={t('user:vat')}
            labelVariant="top"
          />
        )}
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="street"
              label={t('user:street')}
              labelVariant="top"
              required
              next="street2"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="street2"
              label={t('user:street2')}
              labelVariant="top"
            />
          </View>
        </View>
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="zip"
              label={t('user:zip')}
              labelVariant="top"
              required
              next="city"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="city"
              label={t('user:city')}
              labelVariant="top"
              required
            />
          </View>
          <View className="flex-1">
            <FormSelect
              control={form.control}
              name="country_code"
              label={t('user:country')}
              labelVariant="top"
              required
              options={countries}
              placeholder={t('user:address.country')}
            />
          </View>
        </View>
      </View>
    </FormProvider>
  )
}
